/**
 * team-section.js
 * Handles pagination (desktop/tablet) and load more (mobile)
 * for the Our Team Shopify section.
 *
 * Usage: TeamSection.init(sectionId)
 */

var TeamSection = (function () {
  'use strict';

  var MOBILE_BREAKPOINT = 768;

  /**
   * Initialize the team section for a given section ID.
   * @param {string} sectionId - Shopify section.id
   */
  function init(sectionId) {
    var grid        = document.getElementById('team-grid-' + sectionId);
    var pagination  = document.getElementById('team-pagination-' + sectionId);
    var loadMoreWrap = document.getElementById('team-loadmore-wrapper-' + sectionId);
    var loadMoreBtn  = document.getElementById('team-loadmore-btn-' + sectionId);

    var arrowLeft  = pagination.getAttribute('data-arrow-left');
    var arrowRight = pagination.getAttribute('data-arrow-right');

    if (!grid) return;

    var perPage     = parseInt(grid.getAttribute('data-per-page'), 10) || 8;
    var cards       = Array.prototype.slice.call(grid.querySelectorAll('.cp-team-card'));
    var totalCards  = cards.length;
    var currentPage = 1;
    var mobileLoadedCount = 0;
    var mobileInitialCount = Math.min(perPage, totalCards);

    // ─────────────────────────────────────────────
    // Utilities
    // ─────────────────────────────────────────────

    function isMobile() {
      return window.innerWidth < MOBILE_BREAKPOINT;
    }

    function hideAllCards() {
      cards.forEach(function (card) {
        card.style.display = 'none';
      });
    }

    function showCards(startIndex, endIndex) {
      cards.forEach(function (card, i) {
        if (i >= startIndex && i < endIndex) {
          var positionInPage = i - startIndex;
          var positionInRow = positionInPage % 4;
          var delay = positionInRow * 100 + 200;

          card.setAttribute('data-aos-delay', delay);
          card.classList.remove('aos-animate', 'aos-init');

          card.style.display = '';

          // Re-observe only this card
          setTimeout(function (c, d) {
            c.style.transitionDelay = d + 'ms';
            c.classList.add('aos-init');
            setTimeout(function () {
              c.classList.add('aos-animate');
            }, d);
          }, 10, card, delay);

        } else {
          card.style.display = 'none';
        }
      });
    }

    // ─────────────────────────────────────────────
    // Desktop / Tablet — Pagination
    // ─────────────────────────────────────────────

    function getTotalPages() {
      return Math.ceil(totalCards / perPage);
    }

    function goToPage(page, isInitial) {
      var totalPages = getTotalPages();
      if (page < 1 || page > totalPages) return;
      currentPage = page;

      var start = (currentPage - 1) * perPage;
      var end   = Math.min(start + perPage, totalCards);
      showCards(start, end);
      renderPagination();

      // Scroll to top of section smoothly
      if(!isInitial) {
        var section = grid.closest('section');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    }

    /**
     * Build pagination: ‹ 1 2 3 ... 10 ›
     * Always show: first page, last page, current page, ±1 around current.
     */
    function renderPagination() {
      if (!pagination) return;
      pagination.innerHTML = '';

      var totalPages = getTotalPages();
      if (totalPages <= 1) return;

      // Helper: create a button element
      function makeBtn(label, onClick, extraClass, disabled, ariaLabel) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cp-page-btn' + (extraClass ? ' ' + extraClass : '');
        btn.innerHTML = label;
        if (ariaLabel) btn.setAttribute('aria-label', ariaLabel);
        if (disabled) {
          btn.setAttribute('disabled', 'disabled');
        } else if (onClick) {
          btn.addEventListener('click', onClick);
        }
        return btn;
      }

      // Prev arrow
      pagination.appendChild(
        makeBtn('<img src="' + arrowLeft + '" width="18" height="18" alt="Previous">', function () { goToPage(currentPage - 1); },
          'arrow', currentPage === 1, 'Previous page')
      );

      // Page number range to display
      var pages = buildPageRange(currentPage, totalPages);

      pages.forEach(function (p) {
        if (p === '...') {
          pagination.appendChild(makeBtn('&hellip;', null, 'ellipsis'));
        } else {
          var btn = makeBtn(p, function () { goToPage(p); });
          if (p === currentPage) {
            btn.classList.add('active');
            btn.setAttribute('aria-current', 'page');
          }
          pagination.appendChild(btn);
        }
      });

      // Next arrow
      pagination.appendChild(
        makeBtn('<img src="' + arrowRight + '" width="18" height="18" alt="Next">', function () { goToPage(currentPage + 1); },
          'arrow', currentPage === totalPages, 'Next page')
      );
    }

    /**
     * Build an array of page numbers (and '...' ellipsis placeholders).
     * e.g. [1, 2, 3, '...', 10] or [1, '...', 4, 5, 6, '...', 10]
     */
    function buildPageRange(current, total) {
      var delta = 1; // pages shown around current
      var range = [];
      var pages = [];

      for (var i = 1; i <= total; i++) {
        if (
          i === 1 ||
          i === total ||
          (i >= current - delta && i <= current + delta)
        ) {
          range.push(i);
        }
      }

      var prev = null;
      range.forEach(function (p) {
        if (prev !== null && p - prev > 1) {
          pages.push('...');
        }
        pages.push(p);
        prev = p;
      });

      return pages;
    }

    // ─────────────────────────────────────────────
    // Mobile — Load More
    // ─────────────────────────────────────────────

    function initMobile() {
      mobileLoadedCount = mobileInitialCount;

      // Show first batch, hide rest
      cards.forEach(function (card, i) {
        card.style.display = i < mobileLoadedCount ? '' : 'none';
      });

      updateLoadMoreBtn();
    }

    function updateLoadMoreBtn() {
      if (!loadMoreBtn || !loadMoreWrap) return;
      if (mobileLoadedCount >= totalCards) {
        loadMoreBtn.disabled = true;
        loadMoreBtn.textContent = 'No More Members';
        loadMoreBtn.style.display = 'none'
        loadMoreWrap.style.display = mobileLoadedCount > 0 ? 'flex' : 'none';
      } else {
        loadMoreBtn.disabled = false;
        loadMoreBtn.textContent = 'Load More';
        loadMoreWrap.style.display = 'flex';
      }
    }

    function loadMore() {
      var nextCount = Math.min(mobileLoadedCount + perPage, totalCards);
      for (var i = mobileLoadedCount; i < nextCount; i++) {
        cards[i].style.display = '';
      }
      mobileLoadedCount = nextCount;
      updateLoadMoreBtn();
    }

    if (loadMoreBtn) {
      loadMoreBtn.addEventListener('click', loadMore);
    }

    // ─────────────────────────────────────────────
    // Mode switcher: run correct logic based on screen
    // ─────────────────────────────────────────────

    function applyMode() {
      if (isMobile()) {
        // Mobile: Load More mode
        if (pagination) pagination.innerHTML = '';
        if (loadMoreWrap) loadMoreWrap.style.display = '';
        initMobile();
      } else {
        // Desktop / Tablet: Pagination mode
        if (loadMoreWrap) loadMoreWrap.style.display = 'none';
        currentPage = 1;
        goToPage(currentPage, true);
      }
    }

    // Debounce resize for performance
    var resizeTimer = null;

    var lastWidth = window.innerWidth;

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        // Hanya applyMode() jika width berubah, bukan height
        if (window.innerWidth !== lastWidth) {
          lastWidth = window.innerWidth;
          applyMode();
        }
      }, 200);
    });

    // Initial run
    applyMode();
  }

  return {
    init: init
  };

})();
